// src/web/features/materials/hooks/useMaterials.ts
import { useState, useEffect } from 'react';
import { config } from '../../auth/config';

interface Material {
  id: number;
  title: string;
  subject: string;
  classSection: string;
  createdAt: string;
  fileUrl: string;
  // ... other properties
}

interface MaterialFilters {
  subject?: string;
  classSection?: string;
  search?: string;
}

export const useMaterials = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<MaterialFilters>({});

// PUBLIC access - no authentication required for listing materials
const fetchMaterials = async () => {
  try {
    setLoading(true);
    setError(null);

    // Build query parameters
    const params = new URLSearchParams();

    if (filters.subject && filters.subject.trim() !== '') {
      params.append('subject', filters.subject.trim());
    }

    if (filters.classSection && filters.classSection.trim() !== '') {
      params.append('classSection', filters.classSection.trim());
    }

    if (filters.search && filters.search.trim() !== '') {
      params.append('search', filters.search.trim());
    }

    const queryString = params.toString();
    const url = `${config.api.baseUrl}/api/materials${queryString ? `?${queryString}` : ''}`;

    console.log('🚀 Fetching materials from URL:', url);
    console.log('🎯 Applied filters:', filters);

    // Optional auth - add token if available
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    setMaterials(data.materials || []);

    console.log('✅ Materials loaded:', {
      count: data.materials?.length || 0,
      appliedFilters: filters,
      sampleClasses: data.materials?.slice(0, 3).map((m: any) => m.classSection) || []
    });

  } catch (error: any) {
    console.error('❌ Error fetching materials:', error);
    setError(error.message || 'Failed to load materials');
  } finally {
    setLoading(false);
  }
};



  // Add the deleteMaterial function
  const deleteMaterial = async (id: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('No auth token');

      const response = await fetch(`${config.api.baseUrl}/api/materials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete material');
      }

      // Remove the deleted material from the local state
      setMaterials(prev => prev.filter(material => material.id !== id));
      
      return response.json();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [filters]);

  // Return the new function along with existing ones
  return { 
    materials, 
    loading, 
    error, 
    filters, 
    setFilters, 
    refetch: fetchMaterials,
    deleteMaterial  // Add this line
  };
};
